import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Copy, KeyRound, Loader2, LogOut, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iteration 291 — Admin CSR PIN management panel (/admin/csr-pins).
 *
 * Full lifecycle for the 4-digit access PINs that unlock the CSR Family
 * Support Hub (/csr): generate with a label + validity window, list
 * active/expired PINs with status indicators, and expire a PIN instantly.
 * Gated behind the same ADMIN_API_KEY prompt as /admin (shared
 * `mttApp:adminKey` sessionStorage cache).
 */
export default function AdminCsrPins() {
  const [adminKey, setAdminKey] = useState(() =>
    (typeof window !== "undefined" && sessionStorage.getItem("mttApp:adminKey")) || ""
  );
  const [keyInput, setKeyInput] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  const [pins, setPins] = useState([]);
  const [listError, setListError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const [label, setLabel] = useState("");
  const [days, setDays] = useState(30);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [newPin, setNewPin] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expiring, setExpiring] = useState("");

  const hdrs = (k) => ({ "X-Admin-Key": k });

  const loadPins = useCallback(async (k) => {
    setListError("");
    const { data } = await api.get("/admin/csr-pins", { headers: hdrs(k) });
    setPins(data?.pins || []);
  }, []);

  const attemptUnlock = useCallback(async (candidateKey) => {
    if (!candidateKey) return false;
    setUnlocking(true);
    setUnlockError("");
    try {
      await loadPins(candidateKey);
      sessionStorage.setItem("mttApp:adminKey", candidateKey);
      setAdminKey(candidateKey);
      setUnlocked(true);
      return true;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) sessionStorage.removeItem("mttApp:adminKey");
      setUnlockError(
        status === 401
          ? "That admin key wasn't recognised. Try again."
          : (formatApiError(e) || "Couldn't validate key — try again.")
      );
      return false;
    } finally {
      setUnlocking(false);
    }
  }, [loadPins]);

  useEffect(() => {
    if (adminKey && !unlocked) attemptUnlock(adminKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    const trimmed = keyInput.trim();
    if (!trimmed) return;
    const ok = await attemptUnlock(trimmed);
    if (ok) setKeyInput("");
  };

  const lockAndReset = () => {
    sessionStorage.removeItem("mttApp:adminKey");
    setAdminKey("");
    setUnlocked(false);
    setPins([]);
    setNewPin(null);
  };

  const refresh = async () => {
    setRefreshing(true);
    try { await loadPins(adminKey); } catch (e) { setListError(formatApiError(e) || "Couldn't load PINs"); }
    finally { setRefreshing(false); }
  };

  const createPin = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setCreateError("");
    setNewPin(null);
    try {
      const { data } = await api.post(
        "/admin/csr-pins",
        { label: label.trim(), days_valid: Number(days) || 30 },
        { headers: hdrs(adminKey) },
      );
      setNewPin(data);
      setLabel("");
      await loadPins(adminKey);
    } catch (e2) {
      setCreateError(formatApiError(e2) || "Couldn't create PIN — try again.");
    } finally {
      setCreating(false);
    }
  };

  const expirePin = async (pin) => {
    setExpiring(pin);
    try {
      await api.post(`/admin/csr-pins/${pin}/expire`, {}, { headers: hdrs(adminKey) });
      await loadPins(adminKey);
    } catch (e) {
      setListError(formatApiError(e) || `Couldn't expire PIN ${pin}`);
    } finally {
      setExpiring("");
    }
  };

  const copyPin = async (pin) => {
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard unavailable */ }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-brandTeal/5" data-testid="admin-csr-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brandSlate transition" data-testid="admin-csr-back">
            <ArrowLeft className="w-4 h-4" /> Admin console
          </Link>
          <div className="pill bg-teal-100 text-teal-800 inline-flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> CSR Family Support Hub
          </div>
        </div>

        <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-4">CSR Access PINs</h1>
        <p className="text-slate-600 mt-1 text-sm">
          Generate and revoke the 4-digit PINs that unlock the PIN-protected CSR gifting section at <code className="text-xs px-1.5 py-0.5 bg-slate-100 rounded">/csr</code>.
        </p>

        {!unlocked ? (
          <div className="card-base p-6 md:p-8 max-w-lg mt-8" data-testid="admin-csr-unlock-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl bg-brandTeal/10 text-brandTeal inline-flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-semibold text-brandSlate">Admin key required</div>
                <div className="text-xs text-slate-500">Enter the platform admin key to manage CSR PINs.</div>
              </div>
            </div>
            <form onSubmit={handleUnlockSubmit} className="space-y-3" data-testid="admin-csr-unlock-form">
              <input
                type="password"
                className="input-base"
                placeholder="Admin API key"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                data-testid="admin-csr-unlock-input"
              />
              {unlockError && <div className="text-xs text-rose-600" data-testid="admin-csr-unlock-error">{unlockError}</div>}
              <button type="submit" disabled={unlocking} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="admin-csr-unlock-btn">
                {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Unlock PIN manager
              </button>
            </form>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {/* Generate */}
            <div className="card-base p-6" data-testid="csr-pin-create-card">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandSlate mb-1">Generate a new PIN</div>
              <p className="text-sm text-slate-600 mb-4">Give each community partner their own labelled PIN so access can be revoked independently.</p>
              <form onSubmit={createPin} className="flex flex-wrap items-end gap-3" data-testid="csr-pin-create-form">
                <label className="flex-1 min-w-[200px]">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Label</span>
                  <input
                    className="input-base mt-1"
                    placeholder="e.g. Hackney Food Bank"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    maxLength={80}
                    data-testid="csr-pin-label-input"
                  />
                </label>
                <label className="w-36">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Valid for (days)</span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    className="input-base mt-1"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    data-testid="csr-pin-days-input"
                  />
                </label>
                <button type="submit" disabled={creating || !label.trim()} className="btn-primary !py-2.5 inline-flex items-center gap-2" data-testid="csr-pin-create-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Generate PIN
                </button>
              </form>
              {createError && <div className="text-xs text-rose-600 mt-2" data-testid="csr-pin-create-error">{createError}</div>}
              {newPin && (
                <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="csr-pin-new">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">New PIN · {newPin.label}</div>
                    <div className="font-mono text-3xl font-bold tracking-[0.4em] text-emerald-900 mt-1" data-testid="csr-pin-new-digits">{newPin.pin}</div>
                    <div className="text-[11px] text-emerald-700 mt-1">Expires {(newPin.expires_at || "").slice(0, 10)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyPin(newPin.pin)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border border-emerald-300 text-emerald-800 hover:bg-emerald-100 transition"
                    data-testid="csr-pin-copy-btn"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy PIN"}
                  </button>
                </div>
              )}
            </div>

            {/* List */}
            <div className="card-base p-6" data-testid="csr-pin-list-card">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandSlate">Issued PINs</div>
                  <div className="text-sm text-slate-600 mt-1">Active PINs unlock the hub instantly; expired PINs are rejected with 401.</div>
                </div>
                <button
                  type="button"
                  onClick={refresh}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 hover:border-brandSlate transition disabled:opacity-50"
                  data-testid="csr-pins-refresh"
                >
                  {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
                </button>
              </div>
              {listError && <div className="text-xs text-rose-600 mb-3" data-testid="csr-pins-error">{listError}</div>}
              {pins.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-8 text-center text-sm text-slate-500" data-testid="csr-pin-list-empty">
                  No PINs issued yet — generate the first one above.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="csr-pin-list">
                    <thead>
                      <tr className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 text-left">
                        <th className="pb-3">PIN</th>
                        <th className="pb-3">Label</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Expires</th>
                        <th className="pb-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pins.map((p) => (
                        <tr key={p.pin} data-testid={`csr-pin-row-${p.pin}`}>
                          <td className="py-3 font-mono font-bold tracking-[0.3em] text-slate-900">{p.pin}</td>
                          <td className="py-3 text-slate-700">{p.label}</td>
                          <td className="py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${p.status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
                              data-testid={`csr-pin-status-${p.pin}`}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td className="py-3 text-xs text-slate-500">{(p.expires_at || "").slice(0, 10) || "—"}</td>
                          <td className="py-3 text-right">
                            {p.status === "active" && (
                              <button
                                type="button"
                                onClick={() => expirePin(p.pin)}
                                disabled={expiring === p.pin}
                                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl border border-rose-200 text-rose-700 hover:bg-rose-50 transition disabled:opacity-50"
                                data-testid={`csr-pin-expire-${p.pin}`}
                              >
                                {expiring === p.pin ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                Expire PIN
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={lockAndReset}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-600 transition"
                data-testid="admin-csr-lock-btn"
              >
                <LogOut className="w-3.5 h-3.5" /> Lock admin console
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
